import { Schema } from "effect"
import { AgentKind, Effort, ModelsByAgent, Taint } from "./common"
import { MemoryScope, MemoryType } from "./domain/knowledge"
import {
  AgentId,
  AreaId,
  Epoch,
  GoalId,
  InitiativeId,
  MemoryItemId,
  Millis,
  NonNegInt,
  PosInt,
  ProfileId,
  ProjectId,
  RepoId,
  RunId,
  RunnerId,
  Sha256,
  ShortRef,
  TaskId,
  UserId,
  WorkerRef,
  WorkspaceId,
} from "./ids"
import { Handoff, RunKind } from "./run"
import { AccessRequest } from "./runner"

// #region context-pack
/** One line of the "why did it do that?" manifest. Revoking a bad item shows every run that loaded it. */
export const LoadedItem = Schema.Struct({
  memoryItemId: MemoryItemId,
  revision: NonNegInt,
  scope: MemoryScope,
  type: MemoryType,
  reason: Schema.TaggedUnion({ always: {}, trigger: { matched: Schema.String }, summary: {}, on_demand: {} }),
  tokens: NonNegInt,
  tainted: Schema.Boolean,
})

/** The only relative paths a pack may write. The runner rejects anything else (no arbitrary paths, no ".."). */
export const PackPath = Schema.String.check(
  Schema.isPattern(
    /^(AGENTS\.md|CLAUDE\.md|\.memory\/[A-Za-z0-9._/-]+|\.claude\/skills\/[A-Za-z0-9._/-]+|\.agents\/skills\/[A-Za-z0-9._/-]+)$/,
  ),
  Schema.makeFilter((s: string) => !s.includes("..") || "no .. segments"),
)

export const PackFile = Schema.Struct({
  path: PackPath,
  sha256: Sha256,
  bytes: NonNegInt,
  /** Inline below 64 KiB, else fetched from R2 with the run's stream token. */
  content: Schema.TaggedUnion({ inline: { text: Schema.String }, blob: { key: Schema.String } }),
})

/**
 * Assembled in the cloud by core.assembleContext when the run is requested; stored content-addressed in R2.
 * Its hash is signed into the job; the sink streams it in turn.start; the runner verifies before writing.
 */
export const ContextPack = Schema.Struct({
  hash: Sha256, // sha256 over the canonical list of (path, sha256)
  files: Schema.Array(PackFile),
  manifest: Schema.Array(LoadedItem),
  /** Every run re-reads the original goal and contract by reference, never only a parent's summary. */
  ancestry: Schema.Struct({
    goalIds: Schema.Array(GoalId),
    initiativeId: Schema.NullOr(InitiativeId),
    areaId: Schema.NullOr(AreaId),
    taskId: Schema.NullOr(TaskId),
  }),
  handoff: Schema.NullOr(Handoff),
  taint: Taint, // the most tainted thing loaded; decides which grants the run gets (Rule of Two), checked per call
  tokenEstimate: NonNegInt,
})
// #endregion context-pack

// #region job
export const ToolClass = Schema.Literals(["graph", "read", "write", "outward"])

/** One MCP server for the run. The url is always Minga's (Project DO graph endpoint or tool gateway). */
export const McpGrant = Schema.Struct({
  name: Schema.String, // "minga", "openseo"
  url: Schema.String,
  token: Schema.Redacted(Schema.String), // in the signed job and Renew responses only; never in a pack or transcript
  expiresAt: Millis, // ≤ lease.expiresAt + 10 s; refreshed by Renew
  tools: Schema.Array(Schema.Struct({ name: Schema.String, class: ToolClass, schemaHash: Sha256 })),
})

/**
 * HMAC-signed by the cloud (it issues and verifies). Identity only: grants, pins and taint are NOT in the token.
 * The endpoint checks them server-side on EVERY call (with epoch current and run not ended), so revoking a grant
 * or raising taint applies immediately, mid-run.
 */
export const McpTokenClaims = Schema.Struct({
  jti: Schema.String,
  aud: Schema.Literals(["graph", "gateway"]),
  sub: RunId,
  epoch: Epoch,
  actor: Schema.TaggedUnion({ agent: { agentId: AgentId }, worker: { taskId: TaskId, agentId: AgentId } }),
  principal: UserId, // whose grants are used; never a different member's
  projectId: Schema.NullOr(ProjectId),
  areaId: Schema.NullOr(AreaId),
  exp: Millis, // ≤ lease.expiresAt + 10 s
})

export const PermissionProfile = Schema.Literals([
  "read_only",
  "workspace_write",
  "workspace_write_network",
  "full_auto",
])

export const JobWorkspace = Schema.TaggedUnion({
  none: {}, // turns that only use graph tools
  worktree: {
    repoId: RepoId,
    /** Branch name (resolved as origin/<name> first) or a 40-hex sha. */
    baseRef: Schema.String,
    /** ^minga/[a-z0-9][a-z0-9._/-]{0,100}$, checked by the runner. */
    branch: Schema.String,
    reuseOf: Schema.NullOr(TaskId),
    /**
     * The worktree path key <root>/<repo>/<worktreeKey> (A4). Workers: the task id. Validators:
     * `<taskId>-review-<round>`, so a review never lands in the worker's worktree. Absent (older templates) = taskId.
     */
    worktreeKey: Schema.optionalKey(Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]{1,80}$/))),
    /** Offer the runner-local tool `open_pull_request` (W9). Draft PRs only this wave. */
    pullRequest: Schema.optionalKey(Schema.Literal("draft")),
  },
  scratch: {}, // empty temp dir (content work)
})

/**
 * Optional run caps. Each null = no Minga cap: the runner passes nothing and the CLI's own default applies (rev 2.1,
 * decision 0018: no turn or time limits at first). A set `wallClockMs` excludes time in awaiting_permission (W22).
 */
export const RunLimits = Schema.Struct({
  maxTurns: Schema.NullOr(PosInt),
  wallClockMs: Schema.NullOr(PosInt),
})

/** The only thing a runner executes. No shell verb, no absolute path, no environment variable, no home path. */
export const JobSpec = Schema.Struct({
  v: Schema.Literal(1),
  runId: RunId,
  epoch: Epoch,
  kind: RunKind,
  principal: UserId,
  projectId: Schema.NullOr(ProjectId),
  /** The task (workers, validators): RunnerRepo.worktrees and SessionRef.projectKey. The path key is worktreeKey. */
  taskId: Schema.optionalKey(TaskId),
  /** The agent (turns): the turn cwd <data>/agents/<agentId>/. */
  agentId: Schema.optionalKey(AgentId),
  /** "R-12", for logs and `minga status`. */
  runRef: Schema.optionalKey(ShortRef),
  agent: Schema.Struct({
    kind: AgentKind,
    profileId: ProfileId,
    model: Schema.NullOr(Schema.String),
    effort: Schema.NullOr(Effort),
  }),
  workspace: JobWorkspace,
  contextPack: Schema.Struct({ hash: Sha256, bytes: NonNegInt }),
  mcp: Schema.Array(McpGrant),
  permission: PermissionProfile, // limited by the runner's ceiling
  /** Authoritative on the runner (over TurnStart.settings.maxTurns), W22. null = no Minga cap (rev 2.1, the default
   * this wave): the runner passes nothing and the CLI's own default applies. A set wall clock excludes awaiting_permission. */
  limits: RunLimits,
  /** Where the runner dials the run stream: always the run's Run DO (W2), which relays turns to the Agent DO. */
  stream: Schema.Struct({ url: Schema.String, token: Schema.Redacted(Schema.String) }),
  /** publish runs on a runner: exactly the approved call (no model). */
  publish: Schema.NullOr(
    Schema.Struct({
      integration: Schema.String,
      tool: Schema.String,
      argsHash: Sha256,
      toolSchemaHash: Sha256,
    }),
  ),
  notAfter: Millis, // must start before this
  /** Settings wave (decision 0026): narrows the profile for this run; present only when it narrows something. A
   *  runner offers itself for such a job only with the manifest feature `access_request` (core K14). */
  access: Schema.optionalKey(AccessRequest),
  /** The temporary teammate ("W-3"), for logs and `minga status`. Display only. */
  workerRef: Schema.optionalKey(WorkerRef),
})

/**
 * Detached Ed25519 signature by a separate signer; the runner pins its public keys at pairing.
 * `payload` = canonicalJson(Schema.encodeSync(Schema.toCodecJson(JobSpec))(spec)) — the JSON codec, so Redacted
 * tokens are their values (a plain encodeSync keeps Redacted objects). The runner verifies `payload` as received,
 * then decodes it with Schema.toCodecJson(JobSpec). `sig` = base64url Ed25519 signature over its UTF-8 bytes.
 */
export const SignedJob = Schema.Struct({ kid: Schema.String, payload: Schema.String, sig: Schema.String })

/** HMAC-signed by the cloud like McpTokenClaims; authorises one runner to dial one run's stream. */
export const StreamTokenClaims = Schema.Struct({
  jti: Schema.String,
  aud: Schema.Literal("stream"),
  sub: RunId,
  epoch: Epoch,
  runnerId: RunnerId,
  workspaceId: WorkspaceId,
  projectId: Schema.NullOr(ProjectId),
  /** With runnerId, decides whether a SessionRef is resumable on this executor (A3). */
  profileId: ProfileId,
  exp: Millis, // lease.expiresAt + 10 s; re-minted by every Renew (W8), like the MCP token
})

/**
 * The run-specific half of a JobSpec, frozen by the Project DO with the run and carried in Enqueue. At Claim the hub
 * adds runId, epoch, agent.kind/profileId (from the offered profile), mcp, stream and notAfter, then signs. A null
 * taskId/agentId is omitted from the JobSpec (optional keys there).
 */
export const JobTemplate = Schema.Struct({
  v: Schema.Literal(1),
  kind: RunKind,
  principal: UserId,
  projectId: Schema.NullOr(ProjectId),
  areaId: Schema.NullOr(AreaId),
  taskId: Schema.NullOr(TaskId),
  agentId: Schema.NullOr(AgentId),
  runRef: ShortRef,
  /** The MCP grant's actor: turns act as the agent, workers/validators as the worker. */
  actor: McpTokenClaims.fields.actor,
  model: Schema.NullOr(Schema.String),
  effort: Schema.NullOr(Effort),
  workspace: JobWorkspace,
  contextPack: Schema.Struct({ hash: Sha256, bytes: NonNegInt }),
  permission: PermissionProfile,
  /** Authoritative on the runner (W22); null = no Minga cap (rev 2.1, the default this wave). */
  limits: RunLimits,
  publish: JobSpec.fields.publish, // always null this wave
  /** Settings wave: the run's narrowing request (`Run.access`), only when it narrows something. */
  access: Schema.optionalKey(AccessRequest),
  /** Workers/validators: model and effort per CLI family; the hub picks the entry at Claim when the lease decides
   *  the family (`model ?? byAgent[kind].model`, the same for effort). */
  byAgent: Schema.optionalKey(ModelsByAgent),
  /** The temporary teammate (`Run.worker.ref`). Display only. */
  workerRef: Schema.optionalKey(WorkerRef),
})
export type JobTemplate = typeof JobTemplate.Type
// #endregion job
